import { z } from 'zod';
import {
  ageYearsSchema,
  idParamSchema,
  moneySchema,
  optionalPhone,
  optionalString,
  paginationQuerySchema,
  percentageSchema,
  requiredString,
} from './common';
import { genderSchema, orderRequestStatusSchema, paymentMethodSchema } from './enums';
import { refineBilling } from './patient';

/**
 * A doctor asking for tests, before anybody has registered a patient.
 *
 * Almost everything is optional, and that is the point: a request is a name and
 * a list of tests read off a fax. Requiring an age or a phone number would mean
 * the receptionist inventing them to get the request recorded at all, and an
 * invented age is worse than a missing one.
 */

const orderRequestLineSchema = z.object({
  /** Exactly as it was written. Kept even after mapping, for the audit trail. */
  requested_text: requiredString('Test'),
  /**
   * The catalogue entry it means, once somebody decides.
   *
   * Nullable on purpose. A fax says "LFT"; mapping that is the receptionist's
   * job, but the line has to exist before they do it or the record is not what
   * the doctor sent.
   */
  test_id: idParamSchema.nullish(),
});

export const createOrderRequestSchema = z.object({
  patient_name: requiredString('Patient name'),
  phone_number: optionalPhone(),
  age_year: ageYearsSchema.nullish(),
  gender: genderSchema.nullish(),
  referred_by: optionalString(),
  /** "fax", "WhatsApp", "walk-in" — free text, because an enum would be wrong. */
  source: optionalString(),
  note: optionalString(),
  lines: z.array(orderRequestLineSchema).min(1, 'Add at least one test').max(50),
});
export type CreateOrderRequestInput = z.infer<typeof createOrderRequestSchema>;
export const updateOrderRequestSchema = createOrderRequestSchema;
export type UpdateOrderRequestInput = z.infer<typeof updateOrderRequestSchema>;

/**
 * Turning a request into a real registration.
 *
 * Deliberately **not** the whole patient form. The unmapped fields — marital
 * status, CNIC, panels — are things the receptionist fills in at the desk with
 * the patient in front of them, and pre-filling them from a fax would put
 * guesses into a billing record.
 *
 * `test_ids` is sent explicitly rather than derived from the request's lines:
 * the receptionist may have mapped four of five, or the doctor may have asked
 * for something the lab does not do, and the registration is what the patient
 * is actually charged for.
 */
export const registerOrderRequestSchema = z
  .object({
    test_ids: z.array(idParamSchema).min(1, 'Choose at least one test').max(50),
    panel_ids: z.array(idParamSchema).max(20).default([]),
    age_year: ageYearsSchema.default(0),
    age_month: z.coerce.number().int().min(0).max(11).default(0),
    age_days: z.coerce.number().int().min(0).max(31).default(0),
    gender: genderSchema,
    married_status: z.enum(['single', 'married']).default('single'),
    phone_number: optionalPhone(),
    cnic_number: optionalString(),
    email: z.union([z.literal(''), z.email()]).nullish(),
    refer_by: optionalString(),
    discount_rupees: moneySchema.default(0),
    discount_percentage: percentageSchema.default(0),
    // Registering a request books a visit and bills it, so it takes the same
    // charge fields the counter does. Omitting them would give the same patient a
    // different bill depending on which door they came in through.
    tax_percentage: percentageSchema.default(0),
    other_charges: moneySchema.default(0),
    other_charges_label: optionalString(),
    /** Money received now — appended to the payment ledger, like the counter's. */
    payment_amount: moneySchema.default(0),
    payment_method: paymentMethodSchema.nullish(),
    /** Asked at the desk while the patient is standing there, same as at the counter. */
    whatsapp_consent: z.boolean().default(false),
    note: optionalString(),
  })
  // The same rules the counter's form applies, for the same reason: without
  // them an unlabelled charge or an untendered payment returns a 500 instead of
  // a message under the field.
  .superRefine(refineBilling);
export type RegisterOrderRequestInput = z.infer<typeof registerOrderRequestSchema>;

export const declineOrderRequestSchema = z.object({
  reason: requiredString('Reason'),
});
export type DeclineOrderRequestInput = z.infer<typeof declineOrderRequestSchema>;

export const orderRequestQuerySchema = paginationQuerySchema.extend({
  status: orderRequestStatusSchema.optional(),
});
export type OrderRequestQuery = z.infer<typeof orderRequestQuerySchema>;
